import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, formatCurrency } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, Search, RefreshCw, Copy, User, Building2, Pencil, Trash2, CheckCircle2, MoreVertical, UserCog, BarChart3, Users2, ClipboardCheck, ExternalLink } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth, isAdmin, isPartner } from '@/contexts/AuthContext';

export default function Quotes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // 'all' | 'me' | 'unassigned' | <user_id>
  const [allUsers, setAllUsers] = useState([]);
  // Dialog Modifica preventivo (title/subtitle/notes/status)
  const [editQuote, setEditQuote] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', subtitle: '', notes: '', status: 'attivo' });
  const [saving, setSaving] = useState(false);
  // Dialog Riassegna preventivo
  const [assignQuote, setAssignQuote] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignTargetUserId, setAssignTargetUserId] = useState('');
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignUserId, setBulkAssignUserId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  // Aggregations
  const [aggregations, setAggregations] = useState(null);
  const [showAggregations, setShowAggregations] = useState(false);
  const canManage = isAdmin(user) || isPartner(user);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (assigneeFilter && assigneeFilter !== 'all') params.assigned_to = assigneeFilter;
      const r = await api.listQuotes(params);
      setItems(r.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile caricare i preventivi');
    } finally { setLoading(false); }
    if (canManage) {
      try {
        const agg = await api.quotesAggregations();
        setAggregations(agg);
      } catch { /* silent */ }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assigneeFilter]);
  useEffect(() => {
    (async () => {
      try {
        const users = await api.listUsers();
        const filtered = isAdmin(user) ? users : users.filter(u => u.company_id === user?.company_id);
        setAllUsers(filtered);
      } catch { /* ignore */ }
    })();
    /* eslint-disable-next-line */
  }, []);

  const openEdit = (q) => {
    setEditQuote(q);
    setEditForm({ title: q.title || '', subtitle: q.subtitle || '', notes: q.notes || '', status: q.status || 'attivo' });
  };
  const saveEdit = async () => {
    if (!editQuote) return;
    setSaving(true);
    try {
      await api.updateQuote(editQuote.id, editForm);
      toast.success('Preventivo aggiornato');
      setEditQuote(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile aggiornare');
    } finally { setSaving(false); }
  };
  const deleteQuote = async (q) => {
    if (!window.confirm(`Eliminare definitivamente il preventivo "${q.title || 'senza titolo'}"?\nQuesta operazione non è reversibile.`)) return;
    try {
      await api.deleteQuote(q.id);
      toast.success('Preventivo eliminato');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile eliminare');
    }
  };
  const contractualize = (q) => {
    // Naviga al nuovo contratto pre-riempito dal preventivo (uso duplicateQuoteId per il pre-fill)
    navigate(`/contracts/new?duplicateQuoteId=${q.id}`);
    toast.info('Compila il wizard per firmare e chiudere il contratto');
  };
  const markConverted = async (q) => {
    if (!window.confirm('Marcare questo preventivo come "convertito in contratto"?')) return;
    try {
      await api.markQuoteConverted(q.id);
      toast.success('Preventivo marcato come convertito');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile aggiornare lo stato');
    }
  };
  const openAssign = async (q) => {
    setAssignQuote(q);
    setAssignTargetUserId('');
    try {
      const users = await api.listUsers();
      const filtered = isAdmin(user) ? users : users.filter(u => u.company_id === user.company_id);
      setAssignableUsers(filtered);
    } catch {
      setAssignableUsers([]);
    }
  };
  const saveAssign = async () => {
    if (!assignQuote || !assignTargetUserId) { toast.error('Seleziona un utente destinatario'); return; }
    try {
      await api.assignQuote(assignQuote.id, assignTargetUserId);
      toast.success('Preventivo riassegnato');
      setAssignQuote(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile riassegnare');
    }
  };

  // ----- Bulk selection & actions -----
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAll = (filteredList) => {
    const ids = filteredList.map(q => q.id);
    setSelectedIds(prev => {
      const allSelected = ids.every(i => prev.has(i));
      const n = new Set(prev);
      if (allSelected) ids.forEach(i => n.delete(i));
      else ids.forEach(i => n.add(i));
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminare definitivamente ${selectedIds.size} preventivi selezionati?\nOperazione irreversibile.`)) return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await api.deleteQuote(id); ok += 1; } catch { fail += 1; }
    }
    setBulkBusy(false);
    clearSelection();
    if (fail === 0) toast.success(`${ok} preventivi eliminati`);
    else toast.warning(`${ok} eliminati, ${fail} falliti`);
    await load();
  };
  const bulkMarkConverted = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Marcare ${selectedIds.size} preventivi come "convertiti"?`)) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of Array.from(selectedIds)) {
      try { await api.markQuoteConverted(id); ok += 1; } catch { fail += 1; }
    }
    setBulkBusy(false);
    clearSelection();
    if (fail === 0) toast.success(`${ok} preventivi marcati`);
    else toast.warning(`${ok} aggiornati, ${fail} falliti`);
    await load();
  };
  const bulkAssignApply = async () => {
    if (!bulkAssignUserId || selectedIds.size === 0) { toast.error('Seleziona un utente'); return; }
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of Array.from(selectedIds)) {
      try { await api.assignQuote(id, bulkAssignUserId); ok += 1; } catch { fail += 1; }
    }
    setBulkBusy(false);
    setBulkAssignOpen(false);
    setBulkAssignUserId('');
    clearSelection();
    if (fail === 0) toast.success(`${ok} preventivi riassegnati`);
    else toast.warning(`${ok} riassegnati, ${fail} falliti`);
    await load();
  };

  const filtered = items.filter((it) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    const cs = it.customer_snapshot || {};
    const label = `${cs.first_name || ''} ${cs.last_name || ''} ${cs.company_name || ''}`.trim().toLowerCase();
    return (
      label.includes(needle) ||
      (it.title || '').toLowerCase().includes(needle) ||
      (it.created_by_name || '').toLowerCase().includes(needle) ||
      (cs.fiscal_code || '').toLowerCase().includes(needle) ||
      (cs.vat_number || '').toLowerCase().includes(needle)
    );
  });

  const scopeLabel = isAdmin(user)
    ? 'Tutti i preventivi (Super Admin)'
    : isPartner(user)
      ? 'Preventivi della tua azienda'
      : 'I tuoi preventivi';

  return (
    <div>
      <PageHeader
        title="I miei Preventivi"
        description={`Storico completo dei preventivi generati. ${scopeLabel}. Da qui puoi rivederli, duplicarli o accedere alla scheda cliente collegata.`}
        actions={canManage && aggregations ? (
          <Button variant="outline" onClick={() => setShowAggregations(v => !v)} data-testid="quotes-toggle-aggregations">
            <BarChart3 className="size-4 mr-2" />{showAggregations ? 'Nascondi report' : 'Mostra report'}
          </Button>
        ) : null}
      />

      {/* Dashboard aggregata (solo admin/partner) */}
      {canManage && showAggregations && aggregations && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="quotes-aggregations">
          <Card>
            <CardContent className="pt-5">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><ClipboardCheck className="size-3.5" />Totale preventivi</div>
              <div className="text-2xl font-semibold tabular-nums mt-1">{aggregations.total_count}</div>
              <div className="text-sm text-muted-foreground tabular-nums mt-0.5">{formatCurrency(aggregations.total_amount)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <div className="text-xs text-muted-foreground">Per stato</div>
              <div className="space-y-1 mt-1">
                {(aggregations.by_status || []).map(s => (
                  <div key={s.key} className="flex items-center justify-between text-xs">
                    <span className="capitalize">{s.key}</span>
                    <span className="tabular-nums font-medium">{s.count} · {formatCurrency(s.amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><Users2 className="size-3.5" />Top agenti</div>
              <div className="space-y-1 mt-1">
                {(aggregations.by_agent || []).slice(0, 5).map(s => (
                  <div key={s.key} className="flex items-center justify-between text-xs">
                    <span className="truncate max-w-[140px]" title={s.name}>{s.name}</span>
                    <span className="tabular-nums font-medium">{s.count} · {formatCurrency(s.amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per cliente, ragione sociale, CF/P.IVA, titolo…"
            className="pl-9"
            data-testid="quotes-search"
          />
        </div>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="w-full sm:w-[240px]" data-testid="quotes-assignee-filter"><SelectValue placeholder="Assegnatario" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tutti gli assegnatari</SelectItem>
            <SelectItem value="me">📌 Solo i miei</SelectItem>
            <SelectItem value="unassigned">— Senza assegnatario</SelectItem>
            {allUsers.length > 0 && <div className="border-t my-1" />}
            {allUsers.map(u => (
              <SelectItem key={u.id} value={u.id}>{u.full_name || u.email} · <span className="text-muted-foreground">{u.role}</span></SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="quotes-refresh">
          <RefreshCw className={`size-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
        </Button>
      </div>

      <Card>
        {selectedIds.size > 0 && (
          <div className="px-4 py-2.5 border-b bg-primary/5 flex flex-wrap items-center gap-3" data-testid="quotes-bulk-toolbar">
            <div className="flex items-center gap-2 text-sm font-medium">
              <CheckCircle2 className="size-4 text-primary" />
              <span data-testid="quotes-bulk-count">{selectedIds.size}</span> {selectedIds.size === 1 ? 'preventivo selezionato' : 'preventivi selezionati'}
            </div>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={clearSelection}>Deseleziona</Button>
            {canManage && (
              <Button variant="outline" size="sm" onClick={() => setBulkAssignOpen(true)} disabled={bulkBusy} data-testid="quotes-bulk-assign-btn">
                <UserCog className="size-3.5 mr-1" />Riassegna
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={bulkMarkConverted} disabled={bulkBusy} data-testid="quotes-bulk-converted-btn">
              <CheckCircle2 className="size-3.5 mr-1" />Marca convertiti
            </Button>
            <Button variant="destructive" size="sm" onClick={bulkDelete} disabled={bulkBusy} data-testid="quotes-bulk-delete-btn">
              <Trash2 className="size-3.5 mr-1" />Elimina
            </Button>
          </div>
        )}
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="size-4" /> {filtered.length} preventivo{filtered.length === 1 ? '' : 'i'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {items.length === 0
                ? 'Nessun preventivo ancora generato. Crea un nuovo preventivo dalla sezione "Nuovo Contratto".'
                : 'Nessun preventivo corrisponde alla ricerca.'}
            </div>
          ) : (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={filtered.length > 0 && filtered.every(q => selectedIds.has(q.id))}
                        onCheckedChange={() => toggleSelectAll(filtered)}
                        data-testid="quotes-select-all"
                        aria-label="Seleziona tutti"
                      />
                    </TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Titolo</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Creato da</TableHead>
                    <TableHead className="text-right">Totale</TableHead>
                    <TableHead className="text-right w-[180px]">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((it) => {
                    const cs = it.customer_snapshot || {};
                    const label = cs.company_name || `${cs.first_name || ''} ${cs.last_name || ''}`.trim() || '—';
                    const secondary = cs.fiscal_code || cs.vat_number || '';
                    return (
                      <TableRow key={it.id} data-testid={`quote-row-${it.id}`} data-selected={selectedIds.has(it.id) || undefined} className={selectedIds.has(it.id) ? 'bg-primary/5' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(it.id)}
                            onCheckedChange={() => toggleSelect(it.id)}
                            data-testid={`quote-select-${it.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {cs.company_name
                              ? <Building2 className="size-3.5 text-muted-foreground" />
                              : <User className="size-3.5 text-muted-foreground" />}
                            <div>
                              <div className="font-medium">{label}</div>
                              {secondary && <div className="text-xs text-muted-foreground">{secondary}</div>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[220px] truncate" title={it.title}>{it.title || 'Preventivo'}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{(it.created_at || '').slice(0, 16).replace('T', ' ')}</TableCell>
                        <TableCell><Badge variant={it.status === 'convertito' ? 'default' : 'outline'}>{it.status || 'attivo'}</Badge></TableCell>
                        <TableCell className="text-xs">{it.created_by_name || '—'}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{formatCurrency(it.totals?.total || 0)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {it.customer_id && (
                              <Button size="sm" variant="ghost" onClick={() => navigate(`/customers/${it.customer_id}`)} title="Apri scheda cliente" data-testid={`quote-open-customer-${it.id}`}>
                                <User className="size-3.5" />
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => navigate(`/contracts/new?editQuoteId=${it.id}`)} title="Apri e modifica preventivo" data-testid={`quote-open-${it.id}`}>
                              <ExternalLink className="size-3.5 mr-1" />Apri
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => navigate(`/contracts/new?duplicateQuoteId=${it.id}`)} title="Duplica preventivo" data-testid={`quote-duplicate-${it.id}`}>
                              <Copy className="size-3.5" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" data-testid={`quote-actions-${it.id}`} title="Altre azioni">
                                  <MoreVertical className="size-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuLabel className="text-xs text-muted-foreground">Preventivo</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => navigate(`/contracts/new?editQuoteId=${it.id}`)} data-testid={`quote-open-menu-${it.id}`}>
                                  <ExternalLink className="size-3.5 mr-2" /> Apri e modifica
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEdit(it)} data-testid={`quote-edit-${it.id}`}>
                                  <Pencil className="size-3.5 mr-2" /> Modifica metadati
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => contractualize(it)} data-testid={`quote-contractualize-${it.id}`}>
                                  <CheckCircle2 className="size-3.5 mr-2" /> Contrattualizza (firma)
                                </DropdownMenuItem>
                                {it.status !== 'convertito' && (
                                  <DropdownMenuItem onClick={() => markConverted(it)} data-testid={`quote-mark-converted-${it.id}`}>
                                    <CheckCircle2 className="size-3.5 mr-2" /> Marca come convertito
                                  </DropdownMenuItem>
                                )}
                                {canManage && (
                                  <DropdownMenuItem onClick={() => openAssign(it)} data-testid={`quote-assign-${it.id}`}>
                                    <UserCog className="size-3.5 mr-2" /> Riassegna a…
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => deleteQuote(it)} className="text-destructive focus:text-destructive" data-testid={`quote-delete-${it.id}`}>
                                  <Trash2 className="size-3.5 mr-2" /> Elimina
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Modifica metadati preventivo */}
      <Dialog open={!!editQuote} onOpenChange={(o) => !o && setEditQuote(null)}>
        <DialogContent data-testid="quote-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifica preventivo</DialogTitle>
            <DialogDescription>Aggiorna titolo, sottotitolo, note e stato. I prodotti e i totali non sono modificabili qui (usa "Duplica" per rigenerare con modifiche).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Titolo</Label>
              <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="quote-edit-title" />
            </div>
            <div>
              <Label className="text-xs">Sottotitolo</Label>
              <Input value={editForm.subtitle} onChange={(e) => setEditForm({ ...editForm, subtitle: e.target.value })} data-testid="quote-edit-subtitle" />
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} data-testid="quote-edit-notes" />
            </div>
            <div>
              <Label className="text-xs">Stato</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger data-testid="quote-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="in_attesa">In attesa</SelectItem>
                  <SelectItem value="convertito">Convertito</SelectItem>
                  <SelectItem value="rifiutato">Rifiutato</SelectItem>
                  <SelectItem value="scaduto">Scaduto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditQuote(null)}>Annulla</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="quote-edit-save">
              {saving ? <RefreshCw className="size-4 mr-2 animate-spin" /> : <Pencil className="size-4 mr-2" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Riassegna preventivo */}
      <Dialog open={!!assignQuote} onOpenChange={(o) => !o && setAssignQuote(null)}>
        <DialogContent data-testid="quote-assign-dialog">
          <DialogHeader>
            <DialogTitle>Riassegna preventivo</DialogTitle>
            <DialogDescription>Trasferisci la proprietà del preventivo a un altro utente. Se l'utente appartiene a un'altra azienda, il preventivo viene spostato di conseguenza.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs">Utente destinatario</Label>
            <Select value={assignTargetUserId} onValueChange={setAssignTargetUserId}>
              <SelectTrigger data-testid="quote-assign-user"><SelectValue placeholder="Seleziona utente…" /></SelectTrigger>
              <SelectContent>
                {assignableUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.full_name || u.email} · <span className="text-muted-foreground">{u.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignQuote(null)}>Annulla</Button>
            <Button onClick={saveAssign} disabled={!assignTargetUserId} data-testid="quote-assign-save">
              <UserCog className="size-4 mr-2" /> Riassegna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog Bulk Riassegna preventivi */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent data-testid="quotes-bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle>Riassegna {selectedIds.size} preventivi</DialogTitle>
            <DialogDescription>Trasferisci in blocco la proprietà dei preventivi selezionati a un altro utente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs">Utente destinatario</Label>
            <Select value={bulkAssignUserId} onValueChange={setBulkAssignUserId}>
              <SelectTrigger data-testid="quotes-bulk-assign-user"><SelectValue placeholder="Seleziona utente…" /></SelectTrigger>
              <SelectContent>
                {allUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name || u.email} · <span className="text-muted-foreground">{u.role}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignOpen(false)} disabled={bulkBusy}>Annulla</Button>
            <Button onClick={bulkAssignApply} disabled={!bulkAssignUserId || bulkBusy} data-testid="quotes-bulk-assign-save">
              {bulkBusy ? <RefreshCw className="size-4 mr-2 animate-spin" /> : <UserCog className="size-4 mr-2" />}Applica riassegnazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
